import {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Code2,
  Quote,
  Link2,
  Heading2,
  Heading3,
  Check,
  X,
} from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import { cn } from './lib/cn';
import { MENTION_TRIGGER_REGEX, markdownPasteToHtml } from '@clarix/utils';

export interface RichTextEditorHandle {
  clearContent: () => void;
  getHTML: () => string;
  focus: () => void;
  insertText: (text: string) => void;
  insertMention: (user: { id: string; name: string }) => void;
}

interface RichTextEditorProps {
  placeholder?: string;
  onSubmit?: (html: string) => void;
  onPasteFiles?: (files: File[]) => void;
  onChange?: (html: string) => void;
  autofocus?: boolean;
  className?: string;
  /**
   * Called for ArrowUp/ArrowDown/Enter/Escape so an open mention menu can be
   * driven from the keyboard while the editor keeps focus. Return true if the
   * key was consumed (the editor then prevents its default handling).
   */
  onMentionKeyDown?: (key: 'ArrowUp' | 'ArrowDown' | 'Enter' | 'Escape') => boolean;
}

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (
    {
      placeholder = 'Write something...',
      onSubmit,
      onPasteFiles,
      onChange,
      autofocus = false,
      className,
      onMentionKeyDown,
    },
    ref,
  ) => {
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const linkInputRef = useRef<HTMLInputElement>(null);
    // Keep the latest handler in a ref — `handleKeyDown` is captured once by
    // useEditor, so reading the prop directly would go stale on every keystroke.
    const onMentionKeyDownRef = useRef(onMentionKeyDown);
    onMentionKeyDownRef.current = onMentionKeyDown;

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] },
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        }),
        Mention.configure({
          HTMLAttributes: {
            class: 'mention-inline',
          },
          renderText({ node }) {
            const label = node.attrs.label ?? node.attrs.id;
            return `@${label}`;
          },
          renderHTML({ node }) {
            const label = node.attrs.label ?? node.attrs.id;
            return [
              'span',
              {
                class: 'mention-inline',
                'data-user-id': node.attrs.id,
                'data-user-name': label,
              },
              `@${label}`,
            ];
          },
        }),
        Placeholder.configure({ placeholder }),
      ],
      autofocus,
      editorProps: {
        attributes: {
          class: 'tiptap',
        },
        handleKeyDown: (_view, event) => {
          // Let an open mention menu consume navigation keys first.
          if (
            event.key === 'ArrowUp' ||
            event.key === 'ArrowDown' ||
            event.key === 'Enter' ||
            event.key === 'Escape'
          ) {
            if (onMentionKeyDownRef.current?.(event.key)) {
              event.preventDefault();
              return true;
            }
          }
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault();
            onSubmit?.(editor?.getHTML() ?? '');
            return true;
          }
          return false;
        },
        handlePaste: (view, event) => {
          const items = event.clipboardData?.items;
          if (items && onPasteFiles) {
            const files: File[] = [];
            for (const item of Array.from(items)) {
              if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) files.push(file);
              }
            }
            if (files.length > 0) {
              event.preventDefault();
              onPasteFiles(files);
              return true;
            }
          }

          // Inside a code block, markdown is content — paste it verbatim.
          if (view.state.selection.$from.parent.type.spec.code) return false;
          const html = markdownPasteToHtml(
            event.clipboardData?.getData('text/plain') ?? '',
            event.clipboardData?.getData('text/html') ?? '',
          );
          if (!html) return false;
          // An inert document: nothing in it loads or runs while ProseMirror reads it.
          const doc = new window.DOMParser().parseFromString(html, 'text/html');
          const slice = ProseMirrorDOMParser.fromSchema(view.state.schema).parseSlice(doc.body);
          view.dispatch(view.state.tr.replaceSelection(slice).scrollIntoView());
          event.preventDefault();
          return true;
        },
      },
      onUpdate: ({ editor: updatedEditor }) => {
        onChange?.(updatedEditor.getHTML());
      },
    });

    useImperativeHandle(ref, () => ({
      clearContent: () => editor?.commands.clearContent(true),
      getHTML: () => editor?.getHTML() ?? '',
      focus: () => editor?.commands.focus(),
      insertText: (text: string) => {
        if (!editor) return;
        editor.chain().focus().insertContent(text).run();
      },
      insertMention: (user: { id: string; name: string }) => {
        if (!editor) return;
        const { from } = editor.state.selection;
        const fullTextBefore = editor.state.doc.textBetween(0, from, '\n', '\n');
        const triggerMatch = fullTextBefore.match(MENTION_TRIGGER_REGEX);
        const mentionTriggerLength = triggerMatch ? 1 + (triggerMatch[1]?.length ?? 0) : 0;
        const rangeFrom = mentionTriggerLength > 0 ? from - mentionTriggerLength : from;
        editor
          .chain()
          .focus()
          .deleteRange({ from: rangeFrom, to: from })
          .insertContent([
            { type: 'mention', attrs: { id: user.id, label: user.name } },
            { type: 'text', text: ' ' },
          ])
          .run();
      },
    }));

    // Focus on link input when popover opens
    useEffect(() => {
      if (linkOpen) {
        setTimeout(() => linkInputRef.current?.focus(), 50);
      }
    }, [linkOpen]);

    const handleLinkOpen = useCallback(() => {
      if (!editor) return;
      const existing = editor.getAttributes('link').href ?? '';
      setLinkUrl(existing);
      setLinkOpen(true);
    }, [editor]);

    const applyLink = useCallback(() => {
      if (!editor) return;
      const url = linkUrl.trim();
      if (url) {
        const { from, to } = editor.state.selection;
        if (from === to) {
          // No text selected — insert the URL as visible linked text
          editor
            .chain()
            .focus()
            .insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`)
            .run();
        } else {
          // Text is selected — wrap it with the link
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: url })
            .run();
        }
      } else {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
      }
      setLinkOpen(false);
      setLinkUrl('');
    }, [editor, linkUrl]);

    const removeLink = useCallback(() => {
      if (!editor) return;
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setLinkOpen(false);
      setLinkUrl('');
    }, [editor]);

    if (!editor) return null;

    const ToolbarButton = ({
      active,
      onClick,
      title,
      children,
    }: {
      active?: boolean;
      onClick: () => void;
      title: string;
      children: React.ReactNode;
    }) => (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'h-7 w-7 shrink-0',
          active
            ? 'bg-gray-200 text-foreground'
            : 'text-muted-foreground hover:text-foreground'
        )}
        title={title}
        onMouseDown={(e) => {
          e.preventDefault();
          onClick();
        }}
      >
        {children}
      </Button>
    );

    const Separator = () => (
      <div className="w-px h-4 bg-[rgb(var(--border))] mx-0.5 shrink-0" />
    );

    return (
      <div className={cn('flex flex-col border-b border-[rgb(var(--border))]', className)}>
        {/* Toolbar */}
        <div className="flex items-center gap-0.5 px-2 py-1 border-b border-[rgb(var(--border))] bg-[rgb(var(--muted))]/20 flex-wrap">
          <ToolbarButton
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="Bold (Ctrl+B)"
          >
            <Bold className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="Italic (Ctrl+I)"
          >
            <Italic className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            title="Underline (Ctrl+U)"
          >
            <UnderlineIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            title="Strikethrough"
          >
            <Strikethrough className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>

          <Separator />

          <ToolbarButton
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="Bullet List"
          >
            <List className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="Ordered List"
          >
            <ListOrdered className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>

          <Separator />

          <ToolbarButton
            active={editor.isActive('codeBlock')}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            title="Code Block"
          >
            <Code2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            title="Blockquote"
          >
            <Quote className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>

          {/* Link with popover */}
          <Popover
            open={linkOpen}
            onOpenChange={(open) => {
              if (open) {
                handleLinkOpen();
              } else {
                setLinkOpen(false);
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'h-7 w-7 shrink-0',
                  editor.isActive('link')
                    ? 'bg-gray-200 text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                title="Link"
              >
                <Link2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-72 p-2"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="flex items-center gap-1">
                <Input
                  ref={linkInputRef}
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyLink();
                    }
                    if (e.key === 'Escape') {
                      setLinkOpen(false);
                    }
                  }}
                  placeholder="https://..."
                  className="h-8 text-lg"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-green-600 hover:text-green-700"
                  onClick={applyLink}
                  title="Apply link"
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                </Button>
                {editor.isActive('link') && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-red-500 hover:text-red-600"
                    onClick={removeLink}
                    title="Remove link"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Separator />

          <ToolbarButton
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            title="Heading 2"
          >
            <Heading2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>

          <ToolbarButton
            active={editor.isActive('heading', { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            title="Heading 3"
          >
            <Heading3 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </ToolbarButton>
        </div>

        {/* Editor content */}
        <EditorContent editor={editor} />
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';
